import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { opsService, type SecurityEvent } from '../../services/opsService';

export function InfrastructureSecurityPage() {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [status, setStatus] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, st] = await Promise.all([opsService.securityEvents(), opsService.securityStatus()]);
      setEvents(Array.isArray(ev) ? ev : []);
      setStatus(st || {});
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">امنیت میزبان</h1>
          <p className="mt-1 text-sm text-slate-500">
            رخدادهای SSH و وضعیت fail2ban/ufw. عامل فایروال را تغییر نمی‌دهد؛ فقط هشدار می‌دهد.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          تازه‌سازی
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <StatusCard label="fail2ban" value={status.fail2ban || 'unknown'} />
        <StatusCard label="ufw" value={status.ufw || 'unknown'} />
      </div>

      <section className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-slate-900">رویدادهای اخیر</h2>
        {events.length === 0 ? (
          <p className="text-sm text-slate-500">{loading ? 'در حال بارگذاری…' : 'رویدادی ثبت نشده.'}</p>
        ) : (
          <ul className="space-y-3">
            {events.map((e) => (
              <li key={e.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                <div className="font-semibold text-slate-900">
                  <span className="me-2 rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800">{e.severity}</span>
                  {e.title}
                </div>
                <p className="mt-1 text-slate-600">{e.message}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {new Date(e.createdAt).toLocaleString('fa-IR')}
                  {e.sourceIp ? ` · IP: ${e.sourceIp}` : ''} · rule: {e.rule}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatusCard({ label, value }: { label: string; value: string }) {
  const bad = value.includes('inactive') || value.includes('missing');
  return (
    <div className={`rounded-xl p-4 shadow-sm ${bad ? 'bg-amber-50 text-amber-900' : 'bg-white text-slate-900'}`}>
      <div className="text-xs font-medium opacity-70">{label}</div>
      <div className="mt-1 text-lg font-bold">{value}</div>
    </div>
  );
}
